import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { BroadcastService } from "../broadcast-service/broadcast.service";
import { IframeMessage, IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { SessionData } from "./auth.interfaces";
import { LongOperationService } from "./long-operation.service";
import { toSessionData } from "./session-data.mapper";

/** Refresh this long before the session deadline, to absorb the parent round-trip. */
export const REFRESH_BUFFER_MS = 2 * 60 * 1000;
/** Never schedule a refresh sooner than this, to avoid a tight loop on a bogus deadline. */
const MIN_REFRESH_DELAY_MS = 5 * 1000;
/** How often to re-check for the user's return after the session was left to lapse. */
export const IDLE_RECHECK_MS = 60 * 1000;
/** Bound on a single updateSession round-trip, so callers can never hang forever. */
const REFRESH_TIMEOUT_MS = 30 * 1000;

/**
 * Keeps the parent iFirm session alive.
 *
 * The app runs in an iframe and holds no token of its own; posting `updateSession` to the
 * parent resets the session to the full configured login timeout (a true sliding extension).
 * This service schedules one such refresh per window and re-arms on each success.
 *
 * Replaces the old @ng-idle/core + @ng-idle/keepalive pairing, which had to re-poke
 * `resetIdleWatch()` on a timer just to lie to its own idle watcher during long operations.
 * Here idleness is a single timestamp, and a long operation simply overrides it.
 */
@Injectable({ providedIn: "root" })
export class SessionKeepaliveService {
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly longOperationService = inject(LongOperationService);
    private readonly destroyRef = inject(DestroyRef);

    private timerId: ReturnType<typeof setTimeout> | undefined;
    private inFlight: Promise<SessionData | null> | undefined;
    private lastActivityAt = Date.now();
    /** Start of the session window currently being counted for presence. */
    private windowStartedAt = Date.now();
    private paused = false;
    private started = false;
    /** Set by AuthService so a refresh can publish the new session data. */
    private applySession: ((session: SessionData) => void) | undefined;
    private currentSession: (() => SessionData | null) | undefined;

    constructor() {
        // Passive listeners only -- these must never affect scroll/input performance.
        const markActive = () => (this.lastActivityAt = Date.now());
        const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "wheel", "focus"];
        for (const event of events) {
            window.addEventListener(event, markActive, { passive: true });
        }
        const onVisibility = () => {
            if (document.visibilityState === "visible") markActive();
        };
        document.addEventListener("visibilitychange", onVisibility);

        this.destroyRef.onDestroy(() => {
            for (const event of events) {
                window.removeEventListener(event, markActive);
            }
            document.removeEventListener("visibilitychange", onVisibility);
            this.stop();
        });

        // The parent showed a relogin dialog: stop extending until the user completes it.
        BroadcastService.on<IframeMessage>("reloginPrompted")
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.pause());

        // Relogin completed (or the parent swapped the session): resume from the new deadline.
        BroadcastService.on<IframeMessage>("sessionChanged")
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.resume());
    }

    /**
     * Wires the service to AuthService's session state. Kept as an explicit call rather than
     * injecting AuthService, which would create a circular dependency.
     */
    public register(currentSession: () => SessionData | null, applySession: (session: SessionData) => void): void {
        this.currentSession = currentSession;
        this.applySession = applySession;
        this.started = true;
        this.windowStartedAt = Date.now();
        this.schedule();
    }

    /** (Re)arms the refresh timer from the current session's absolute deadline. */
    private schedule(): void {
        this.clearTimer();
        if (!this.started || this.paused) return;
        const expiration = this.currentSession?.()?.sessionExpiration;
        if (!expiration) return;

        const delay = Math.max(expiration - REFRESH_BUFFER_MS - Date.now(), MIN_REFRESH_DELAY_MS);
        this.timerId = setTimeout(() => {
            void this.onTimer();
        }, delay);
    }

    private async onTimer(): Promise<void> {
        if (this.isUserPresent()) {
            await this.refresh().catch(() => undefined);
            // schedule() runs off the refreshed deadline; on failure it re-arms off the old one.
            this.schedule();
        } else {
            // User is gone and nothing is running: let the parent's login timeout do its job.
            // Re-check periodically in case they come back before the session actually lapses.
            this.timerId = setTimeout(() => void this.onTimer(), IDLE_RECHECK_MS);
        }
    }

    /**
     * Presence means "acted at least once during the window we are about to extend" -- the same
     * sliding semantics the host's login timeout uses. A fixed idle threshold shorter than the
     * refresh interval would classify a continuously-active user as gone at every tick.
     */
    private isUserPresent(): boolean {
        return this.lastActivityAt > this.windowStartedAt || this.longOperationService.shouldOverrideIdle();
    }

    /**
     * Refreshes only if the session is inside the buffer window; otherwise a no-op.
     * Concurrent callers share one in-flight round-trip.
     */
    public async ensureFresh(): Promise<void> {
        const expiration = this.currentSession?.()?.sessionExpiration;
        if (expiration && expiration - REFRESH_BUFFER_MS > Date.now()) {
            return;
        }
        await this.refresh().catch(() => undefined);
        this.schedule();
    }

    /** Forces one updateSession round-trip, deduplicated across concurrent callers. */
    public refresh(): Promise<SessionData | null> {
        if (!this.inFlight) {
            this.inFlight = this.postUpdateSession().finally(() => (this.inFlight = undefined));
        }
        return this.inFlight;
    }

    private async postUpdateSession(): Promise<SessionData | null> {
        // Each extension opens a new window; presence must be re-earned within it.
        this.windowStartedAt = Date.now();
        // postMessageResp gives msgId correlation, a 5s ack and a response timeout -- the old
        // keepalive used bare postMessage(), so a dropped message looked like a slow parent.
        const result = await this.iframeMessagesService.postMessageResp("updateSession", null, "sessionUpdated", REFRESH_TIMEOUT_MS);
        const session = toSessionData(result as Record<string, unknown>);
        // The parent may extend without re-issuing a session name; keep the existing one.
        const previous = this.currentSession?.();
        if (previous) {
            session.site ||= previous.site;
            session.sessionName ||= previous.sessionName;
            session.userId ||= previous.userId;
        }
        this.applySession?.(session);
        return session;
    }

    private pause(): void {
        this.paused = true;
        this.clearTimer();
    }

    private resume(): void {
        this.paused = false;
        // Completing a relogin is itself an act of presence.
        this.lastActivityAt = Date.now();
        this.windowStartedAt = Date.now();
        this.schedule();
    }

    public stop(): void {
        this.started = false;
        this.clearTimer();
    }

    private clearTimer(): void {
        if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = undefined;
        }
    }
}
